import React, {useEffect, useMemo, useState} from 'react';
import {Tooltip, Button, Modal, Popover, Avatar, Image} from 'antd';
import {
    FiEdit2,
    FiTrash2,
    FiCalendar,
    FiChevronDown,
    FiChevronUp, FiAlertCircle, FiPaperclip,
} from 'react-icons/fi';
import {CgMoreVertical} from "react-icons/cg";
import {BsArrowsMove} from "react-icons/bs";
import {DownloadOutlined, ExclamationCircleOutlined, InfoCircleOutlined, EyeOutlined} from "@ant-design/icons";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
    MeasuringStrategy
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import TaskActions from "@/components/taskManagement/TaskActions";
import TaskFilterHeader from "@/components/taskManagement/TaskFilterHeader";
import {isAfter, isBefore, isToday, startOfWeek, endOfWeek, addWeeks} from 'date-fns';
import {useLocalStorageState} from "ahooks";
import {FileAttachments} from "@/components/taskManagement/FileAttachments";
import {StatusDropdown} from "@/components/taskManagement/StatusDropdown";
import {getPriorityById, PriorityDropdown} from "@/components/taskManagement/PriorityDropdown";
import {TaskDetailsDrawer} from "@/components/taskManagement/TaskDetailsDrawer";
import {TiTick} from "react-icons/ti";


const SortableTask = ({
                          task,
                          listId,
                          onEditTask,
                          onDeleteTask,
                          onMoveTask,
                          TaskActions,
                          lists,
                          list,
                          statuses,
                          openStatusModal,
                          onUpdateTask,
                          onTaskClick
                      }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: `task-${task.id}`,
        data: {
            type: 'task',
            task,
            listId,
            sortable: {
                containerId: `list-${listId}`
            }
        },
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 300ms ease, opacity 300ms ease',
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div ref={setNodeRef} style={style} className="task-wrapper w-full group">
            <div className='rt-taskList__group group-hover:bg-gray-200'>
                <div style={{borderLeft: `6px solid ${list.color}`}} onClick={() => onTaskClick(task)}
                     className='rt-taskList__Name !basis-[200px] !md:basis-[395px] w-[200px] hover:bg-gray-200 group-hover:bg-gray-200 relative'>
                    <div
                        {...attributes}
                        {...listeners}
                        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-400 rounded"
                    >
                        <CgMoreVertical className="h-5 w-5 text-gray-400"/>
                    </div>
                    <h3 className='cursor-pointer hover:bg-gray-200 group-hover:bg-gray-200'>
                        {task.is_private ? 'Private Task: ' : ''} {task.title}
                    </h3>
                    <InfoCircleOutlined
                        className={"ml-1 text-xs cursor-pointer absolute top-3 right-2"}
                    />
                </div>

                <div className='rt-taskList__OtherItems'>
                    <div className='rt-assigneeImage__Column group-hover:bg-gray-100'>
                        {task.assignee ? <Tooltip title={task.assignee.name}
                                                  className={`w-6 h-6 font-normal text-white ${!task.assignee.profile_image ? 'bg-blue-500 text-capitalize' : 'group-hover:bg-gray-100'}`}>
                            <Avatar
                                src={`/${task.assignee.profile_image}`}>
                                {!task.assignee.profile_image && (task.assignee.name.charAt(0))}
                            </Avatar>
                        </Tooltip> : '-'}
                    </div>

                    <div className='rt-Status__Column !p-0'>
                        <StatusDropdown
                            task={task}
                            statuses={statuses}
                            onUpdateTask={onUpdateTask}
                            openStatusModal={openStatusModal}
                        />
                    </div>

                    <div className='rt-dueDate__Column !p-0'>
                        {task.due_date ? (
                            <span className={`flex items-center gap-1 text-[13px] font-normal ${
                                new Date(task.due_date) < new Date() ? 'text-red-500' : ''
                            }`}>
                                <FiCalendar className="h-3.5 w-3.5"/>
                                {new Date(task.due_date).toLocaleDateString()}
                            </span>
                        ) : '-'}
                    </div>

                    <div className='rt-Status__Column  !p-0'>
                        <PriorityDropdown
                            task={task}
                            onUpdateTask={onUpdateTask}
                        />

                    </div>


                    <div>
                        {task?.attachments?.length > 0 && (
                            <FileAttachments attachments={task.attachments}/>
                        )}
                    </div>
                    <div>
                        <span className="flex items-center gap-1 text-[13px] font-normal">{task.budget || '-'}</span>
                    </div>

                    <div>
                        <TaskActions
                            task={task}
                            listId={listId}
                            lists={lists}
                            onEditTask={onEditTask}
                            onDeleteTask={onDeleteTask}
                            onMoveTask={onMoveTask}
                            statuses={statuses}
                            openStatusModal={openStatusModal}
                            onUpdateTask={onUpdateTask}
                        />
                    </div>
                    <div>
                        {task.annotation_count ? <Tooltip title={`Total ${task.annotation_count}  Task Annotation(s)`}>
                            <Button
                                type="text"
                                danger
                                icon={<TiTick className="h-[25px] w-[25px] text-green-800"/>}
                                className="h-10 w-10 flex items-center justify-center hover:bg-red-50"
                            />
                        </Tooltip> : null}
                    </div>
                </div>
            </div>
        </div>
    );
};


const SortableListItem = ({list, children}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: `list-${list.id}`,
        data: {
            type: 'list',
            list
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: transition || 'transform 300ms ease, opacity 300ms ease',
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="rt-taskList__Box p-4 bg-white rounded-lg shadow-md"
        >
            {children(attributes, listeners)}
        </div>
    );
};


const EnhancedListView = ({
                              lists,
                              statuses,
                              onUpdateTask,
                              onDeleteTask,
                              onToggleVisibility,
                              onMoveTask,
                              onAddTask,
                              onEditTask,
                              onEditList,
                              onDeleteList,
                              activeId,
                              activeItem,
                              handleDragStart,
                              handleDragMove,
                              handleDragEnd,
                              openStatusModal,
                              users,
                              currentUser,
                          }) => {

    const [selectedTask, setSelectedTask] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const handleTaskClick = (task) => {
        setSelectedTask(task);
        setIsDrawerOpen(true);
    };

    // Filter states
    const [filterType, setFilterType] = useLocalStorageState('lsk-my-tasks', {
        defaultValue: 'my-tasks'
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedPriorities, setSelectedPriorities] = useState([]);
    const [dueFilter, setDueFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Get all tasks from all lists
    const allTasks = useMemo(() => {
        return lists.flatMap(list =>
            list.tasks.map(task => ({
                ...task,
                list_id: list.id,
                list_name: list.name,
                list_color: list.color
            }))
        );
    }, [lists]);

    // Filter tasks based on current filters
    const filteredTasks = useMemo(() => {
        let filtered = [...allTasks];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(task =>
                task.title?.toLowerCase().includes(query) ||
                task.description?.toLowerCase().includes(query)
            );
        }


        // Apply user filter
        switch (filterType) {
            case 'my-tasks':
                filtered = filtered.filter(task => task.assigned_to === users[0]?.id);
                break;
            case 'by-user':
                if (selectedUser) {
                    filtered = filtered.filter(task => task.assigned_to === selectedUser);
                }
                break;
            default:
                break;
        }

        // Apply status filter
        if (selectedStatuses.length > 0) {
            filtered = filtered.filter(task => selectedStatuses.includes(task.status_id));
        }

        // Apply priority filter
        if (selectedPriorities.length > 0) {
            filtered = filtered.filter(task => selectedPriorities.includes(task.priority));
        }

        // Apply due date filter
        switch (dueFilter) {
            case 'today':
                filtered = filtered.filter(task => task.due_date && isToday(new Date(task.due_date)));
                break;
            case 'this-week':
                filtered = filtered.filter(task => {
                    if (!task.due_date) return false;
                    const due = new Date(task.due_date);
                    const weekStart = startOfWeek(new Date());
                    const weekEnd = endOfWeek(new Date());
                    return isAfter(due, weekStart) && isBefore(due, weekEnd);
                });
                break;
            case 'next-week':
                filtered = filtered.filter(task => {
                    if (!task.due_date) return false;
                    const due = new Date(task.due_date);
                    const nextWeekStart = startOfWeek(addWeeks(new Date(), 1));
                    const nextWeekEnd = endOfWeek(addWeeks(new Date(), 1));
                    return isAfter(due, nextWeekStart) && isBefore(due, nextWeekEnd);
                });
                break;
            case 'overdue':
                filtered = filtered.filter(task =>
                    task.due_date && isBefore(new Date(task.due_date), new Date())
                );
                break;
            case 'no-date':
                filtered = filtered.filter(task => !task.due_date);
                break;
            default:
                break;
        }

        return filtered;
    }, [allTasks, filterType, selectedUser, selectedStatuses, selectedPriorities, dueFilter, users, searchQuery]);


    // Create virtual lists based on filtered tasks
    const virtualLists = useMemo(() => {
        // First sort the lists by position
        const sortedLists = [...lists].sort((a, b) => {
            // Handle undefined positions by placing them at the end
            if (a.position === undefined && b.position === undefined) return 0;
            if (a.position === undefined) return 1;
            if (b.position === undefined) return -1;
            return a.position - b.position;
        });

        // Create the lists map from sorted lists
        const listsMap = sortedLists.reduce((acc, list) => {
            acc[list.id] = {
                ...list,
                tasks: []
            };
            return acc;
        }, {});

        // Add filtered tasks to their respective lists
        filteredTasks.forEach(task => {
            if (listsMap[task.list_id]) {
                listsMap[task.list_id].tasks.push(task);
            }
        });

        // Return as array maintaining the sorted order
        return sortedLists.map(list => ({
            ...listsMap[list.id]
        }));
    }, [lists, filteredTasks]);


    const [expandedLists, setExpandedLists] = useState({});

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDeleteList = (list, e) => {
        e?.stopPropagation();
        Modal.confirm({
            title: 'Delete List',
            icon: <ExclamationCircleOutlined/>,
            content: (
                <div>
                    <p>{`Are you sure you want to delete "${list.name}"?`}</p>
                    <p className="text-red-600">
                        {list.tasks?.length > 0
                            ? `This list contains ${list.tasks.length} task${list.tasks.length === 1 ? '' : 's'}. They will be deleted as well.`
                            : ''}
                    </p>
                    <p className="font-medium">This action cannot be undone.</p>
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: () => onDeleteList(list.id)
        });
    };

    const renderDragOverlay = () => {
        if (!activeItem) return null;

        if (activeId?.toString().startsWith('list-')) {
            return (
                <div className="border border-gray-200 rounded-xl shadow bg-white/90 backdrop-blur-sm p-6">
                    <div className="flex items-center gap-3">
                        <BsArrowsMove className="h-5 w-5 text-gray-400"/>
                        <h3 className="font-medium text-gray-900 m-0">
                            {activeItem.name}
                            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs">
                                {activeItem.tasks?.length || 0} tasks
                            </span>
                        </h3>
                    </div>
                </div>
            );
        }

        // Task overlay
        if (activeId?.toString().startsWith('task-')) {
            const list = lists.find(l => l.id === activeItem.list_id);

            return (
                <div className="task-wrapper w-full">
                    <div className='rt-taskList__group bg-white shadow-lg border border-gray-200 rounded-md'>
                        <div style={{borderLeft: `6px solid ${list?.color || '#000'}`}} className='rt-taskList__Name'>
                            <div className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-400 rounded">
                                <CgMoreVertical className="h-5 w-5 text-gray-400"/>
                            </div>
                            <h3 className='cursor-pointer'>
                                {activeItem.title}
                                {activeItem.description && (
                                    <Tooltip title={activeItem.description} className={"font-normal"}>
                                        <InfoCircleOutlined className={"ml-1 text-xs text-gray-500"}/>
                                    </Tooltip>
                                )}
                            </h3>
                        </div>

                        <div className='rt-taskList__OtherItems'>
                            <div className='rt-assigneeImage__Column'>
                                {activeItem.assignee ?
                                    <Tooltip title={activeItem.assignee.name} className={"font-normal"}>
                                        <Avatar src={`/${activeItem.assignee.profile_image}`}/>
                                    </Tooltip>
                                    : '-'
                                }
                            </div>

                            <div className='rt-Status__Column'>
                                {activeItem.status ?
                                    <Button
                                        className={`text-white inline-flex w-full justify-center items-center text-xs font-normal`}
                                        style={{backgroundColor: activeItem.status.color}}
                                    >
                                        {activeItem.status.name}
                                    </Button>
                                    : '-'
                                }
                            </div>

                            <div className='rt-dueDate__Column'>
                                {activeItem.due_date ? (
                                    <span className={`flex items-center gap-1 text-xs font-normal ${
                                        new Date(activeItem.due_date) < new Date() ? 'text-red-500' : ''
                                    }`}>
                                        <FiCalendar className="h-3.5 w-3.5"/>
                                        {new Date(activeItem.due_date).toLocaleDateString()}
                                    </span>
                                ) : '-'}
                            </div>

                            <div className='rt-Status__Column'>
                                <Button
                                    className={`text-white inline-flex w-full justify-center items-center text-xs font-normal`}
                                    style={{backgroundColor: getPriorityById(activeItem.priority).bgColor}}
                                >
                                    {getPriorityById(activeItem.priority).name}
                                </Button>
                            </div>

                            <div>
                                {activeItem?.attachments?.length > 0 && (
                                    <Button type="text" className="flex items-center space-x-1 text-xs">
                                        <FiPaperclip className="h-3.5 w-3.5"/>
                                        <span>{activeItem.attachments.length}</span>
                                    </Button>
                                )}
                            </div>
                            <div>
                                <span className="flex items-center gap-1 text-xs font-normal">
                                    {activeItem.budget || '-'}
                                </span>
                            </div>
                            <div>
                                <Button type="text" icon={<CgMoreVertical className="h-4 w-4"/>}/>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <>
            <TaskFilterHeader
                users={users}
                selectedUser={selectedUser}
                filterType={filterType}
                onFilterTypeChange={setFilterType}
                onUserSelect={setSelectedUser}
                dueFilter={dueFilter}
                onDueFilterChange={setDueFilter}
                statuses={statuses}
                selectedStatuses={selectedStatuses}
                onStatusChange={setSelectedStatuses}
                selectedPriorities={selectedPriorities}
                onPriorityChange={setSelectedPriorities}
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
            />
            <div className='rt-Tasks__Box__Holder overflow-x-auto grow'>


                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                    measuring={{
                        droppable: {
                            strategy: MeasuringStrategy.Always,
                            frequency: 10,
                        }
                    }}
                >
                    <SortableContext
                        items={virtualLists.map(list => `list-${list.id}`)}
                        strategy={verticalListSortingStrategy}
                    >
                        {virtualLists.map(list => (
                            <>
                                <SortableListItem
                                    key={`list-${list.id}`}
                                    list={list}
                                >
                                    {(attributes, listeners) => (
                                        <>
                                            <>
                                                <div className='rt-taskLIst__Head'>
                                                    <div className='rt-taskList__TitleGroup flex justify-between'>
                                                        <div className='rt-taskList__ButtonHolder'>
                                                            <div
                                                                {...attributes}
                                                                {...listeners}
                                                                className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
                                                            >
                                                                <CgMoreVertical className="h-5 w-5 text-gray-400"/>
                                                            </div>

                                                            <button
                                                                className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                                                                onClick={() => setExpandedLists(prev => ({
                                                                    ...prev,
                                                                    [list.id]: !prev[list.id]
                                                                }))}
                                                            >
                                                                {!expandedLists[list.id] ? (
                                                                    <FiChevronUp className="h-5 w-5 text-gray-600"/>
                                                                ) : (
                                                                    <FiChevronDown
                                                                        className="h-5 w-5 text-gray-600"/>
                                                                )}
                                                            </button>
                                                            <h2 style={{color: list.color}}>
                                                                {list.name}
                                                                <Tooltip title="Edit List">
                                                                    <Button
                                                                        type="text"
                                                                        icon={<FiEdit2
                                                                            className="h-[12px] w-[12px]"/>}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onEditList(list);
                                                                        }}
                                                                        className="hover:bg-gray-100"
                                                                    />
                                                                </Tooltip>
                                                                <span className="ml-2 px-2 py-0.5  text-xs">
                                                                {list.tasks?.length || 0} tasks
                                                            </span>
                                                            </h2>
                                                        </div>

                                                        <Tooltip title="Delete List">
                                                            <Button
                                                                type="text"
                                                                danger
                                                                icon={<FiTrash2 className="h-[12px] w-[12px]"/>}
                                                                onClick={(e) => handleDeleteList(list, e)}
                                                                className="hover:bg-red-50"
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                    {!expandedLists[list.id] && (
                                                        <div className='rt-taskList__itemsNames'>
                                                            <div style={{borderLeft: `6px solid ${list.color}`}}
                                                                 className='rt-taskList__Name !basis-[200px] !md:basis-[395px] w-[200px]'>
                                                                <h3>Task Name</h3>
                                                            </div>
                                                            <div className='rt-taskList__OtherItems'>
                                                                <div>
                                                                    <h4>Assignee</h4>
                                                                </div>
                                                                <div>
                                                                    <h4 className='truncate'>Status</h4>
                                                                </div>
                                                                <div>
                                                                    <h4 className='truncate'>Due date</h4>
                                                                </div>
                                                                <div>
                                                                    <h4 className='truncate'>Priority</h4>
                                                                </div>

                                                                <div className='rt-Status__Column'>
                                                                    <h4 className='truncate'>Attachment</h4>
                                                                </div>
                                                                <div className='rt-Status__Column'>
                                                                    <h4 className='truncate'>Budget</h4>
                                                                </div>
                                                                <div className='rt-Status__Column'>
                                                                    <h4 className='truncate'>Actions</h4>
                                                                </div>
                                                                <div className='rt-Status__Column'>
                                                                    <h4 className='truncate'>Annotations</h4>
                                                                </div>
                                                            </div>
                                                        </div>)}
                                                </div>
                                            </>
                                            {!expandedLists[list.id] &&
                                                <div className='rt-taskList__Body'>
                                                    <SortableContext
                                                        items={list.tasks.map(task => `task-${task.id}`)}
                                                        strategy={verticalListSortingStrategy}
                                                        id={`list-${list.id}`}
                                                    >
                                                        {list.tasks?.length > 0 ? (
                                                            list.tasks.map(task => (
                                                                <SortableTask
                                                                    key={`task-${task.id}`}
                                                                    task={task}
                                                                    list={list}
                                                                    listId={list.id}
                                                                    onEditTask={onEditTask}
                                                                    onDeleteTask={onDeleteTask}
                                                                    onMoveTask={onMoveTask}
                                                                    TaskActions={TaskActions}
                                                                    lists={virtualLists}
                                                                    statuses={statuses}
                                                                    openStatusModal={openStatusModal}
                                                                    onUpdateTask={onUpdateTask}
                                                                    onTaskClick={handleTaskClick}
                                                                />
                                                            ))
                                                        ) : (
                                                            <div className="text-center text-gray-500 py-4">
                                                                No tasks in this list yet
                                                            </div>
                                                        )}
                                                    </SortableContext>
                                                    <div className='rt-Add__Item__Holder'>
                                                        <div className='rt-Add__Item__Area'>
                                                            <div style={{borderLeft: `6px solid ${list.color}`}}
                                                                 className='rt-AddInput__CheckBox'>
                                                                <div className='rt-Add__Item__Input'><span></span>
                                                                </div>
                                                                <div className='rt-Add__Input__Holder'>
                                                                    <input onClick={(ev) => {
                                                                        ev.stopPropagation();
                                                                        onAddTask(list.id);
                                                                    }} type='text' name='add-item'
                                                                           placeholder='+ Add item'/>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>}
                                        </>
                                    )}
                                </SortableListItem>
                            </>
                        ))}
                    </SortableContext>

                    <DragOverlay>
                        {renderDragOverlay()}
                    </DragOverlay>
                </DndContext>

                <TaskDetailsDrawer
                    task={selectedTask}
                    isOpen={isDrawerOpen}
                    onClose={() => {
                        setIsDrawerOpen(false);
                        setSelectedTask(null);
                    }}
                    currentUser={currentUser}
                    users={users}
                />
            </div>
        </>
    );
};

export default EnhancedListView;
